import {
  Modal,
  ModalContent,
  ModalOverlay,
  HStack,
  Box,
  Text,
  VStack,
  Skeleton
} from '@chakra-ui/react';
import React, { FC, useCallback, useState } from 'react';

import { useWithdrawLiquidity } from '@app/hooks/plugins/useWithdrawLiquidity';
import { DebouncedInput } from '@app/components/DebouncedInput';
import fromExponential from 'from-exponential';
import { QuickAmountButtons } from '@app/components/Swap/components/QuickAmountButtons';
import { parseUnits, toOptionalFixed } from '@app/helpers/format';
import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/alert';
import { CopyButton } from '@app/components/CopyButton';
import { useAddress, useSigner } from '@thirdweb-dev/react';
import { SubmitButton } from '@app/components/SubmitButton';
import { BigNumber } from 'ethers';
import { Vault } from '@app/hooks/plugins/useVaults';
import { Pool } from '@app/types/pool';
import { useWithdrawFromIchiVault } from '@app/hooks/ichi/useWithdrawFromIchiVault';
import { useWithdrawFromVault } from '@app/hooks/gamma/useWithdrawFromVault';
import { trimDecimalsOverflow } from '@app/helpers/decimals';

interface Props {
  onClose: () => void;
  isOpen: boolean;
  data: {
    totalAmounts: [BigNumber, BigNumber] | undefined;
    totalSupply: BigNumber | undefined;
    balance: BigNumber | undefined;
    token0: BigNumber | undefined;
    token1: BigNumber | undefined;
    vault: Vault;
  };
  poolInfo: Pool;
}

function shrinkDecimalValue(numString: string, precision: number): string {
  const num = parseFloat(numString);

  if (isNaN(num)) {
    return '';
  }

  // Use toFixed method to shrink the value to a given amount of decimals
  return fromExponential(parseFloat(num.toFixed(precision)));
}

export const VaultPositionActionsModal: FC<Props> = ({
  onClose,
  isOpen,
  data,
  poolInfo
}) => {
  const address = useAddress();
  const [amount, setAmountValue] = useState('');
  const { balance, output, withdraw, withdrawingLiquidity } =
    useWithdrawLiquidity(data, amount, poolInfo);

  const { mutateAsync: withdrawFromIchi, isLoading } =
    useWithdrawFromIchiVault();

  const signer = useSigner();
  const { mutateAsync: withdrawFromGamma, isLoading: withdrawingFromGamma } =
    useWithdrawFromVault();

  const setAmount = useCallback(
    (newValue: string) => {
      setAmountValue(
        trimDecimalsOverflow(
          +newValue > +balance
            ? shrinkDecimalValue(newValue, +data.vault.lpToken.decimals)
            : newValue,
          +data.vault.lpToken.decimals
        )
      );
    },
    [balance, data.vault.lpToken.decimals]
  );

  function renderInfoRow(
    label: string,
    value: string | number,
    loading?: boolean,
    copyValue?: boolean,
    rawValue?: string
  ) {
    function renderContent() {
      if (loading) {
        return (
          <Skeleton
            height="16px"
            width="64px"
            startColor="neutral.700"
            endColor="neutral.800"
            mb="2px"
          />
        );
      }

      return copyValue && rawValue ? (
        <HStack>
          <Text>{value}</Text>
          <CopyButton text={rawValue} />
        </HStack>
      ) : (
        <Text>{value}</Text>
      );
    }

    return (
      <HStack justifyContent="space-between" mb={2} color="whiteOpacity.70">
        <Text fontWeight={400} fontSize="14px">
          {label}
        </Text>
        {renderContent()}
      </HStack>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      motionPreset="slideInBottom"
      size="xs"
    >
      <ModalOverlay />
      <ModalContent sx={{ maxWidth: ['100vw', '600px'] }}>
        <VStack w="100%">
          <Box w="100%">
            <Text
              textAlign="left"
              w="100%"
              color="neutral.300"
              fontSize="12px"
              lineHeight="18px"
              letterSpacing="-0.18px"
              mb="4px"
            >
              Amount
            </Text>
            <DebouncedInput
              textAlign="left"
              amount={amount}
              onChange={val => {
                setAmount(fromExponential(val));
              }}
            />
            <HStack w="100%" justify="space-between">
              <HStack justify="flex-start">
                <Text
                  color="neutral.500"
                  fontSize="12px"
                  fontWeight="400"
                  lineHeight="18px"
                  letterSpacing="-0.18px"
                >
                  Balance:
                </Text>
                <Text>{fromExponential(balance)}</Text>
              </HStack>
            </HStack>
          </Box>
          <QuickAmountButtons
            amount={amount}
            balance={+balance}
            onUpdate={val => setAmount(val)}
            onMax={() => setAmount(balance)}
          />
          <Box
            borderTop="1px solid"
            borderColor="whiteOpacity.6"
            pt="12px"
            w="100%"
          >
            {renderInfoRow(
              `${poolInfo.token0.symbol} amount`,
              output?.amount0 !== undefined
                ? toOptionalFixed(Number(output?.amount0), 5)
                : 'n/a',
              false
            )}
            {renderInfoRow(
              `${poolInfo.token1.symbol} amount`,
              output?.amount1 !== undefined
                ? toOptionalFixed(Number(output?.amount1), 5)
                : 'n/a',
              false
            )}
          </Box>
          {+amount > +balance.toString() && (
            <Alert
              status="error"
              display="flex"
              border="1px solid"
              borderColor="danger.800"
              mt="16px"
              borderRadius="10px"
              py="10px"
              px="12px"
            >
              <AlertIcon w="16px" h="16px" mr="8px" color="orange" />
              <AlertDescription color="danger.200">
                Available balance exceeded
              </AlertDescription>
            </Alert>
          )}
        </VStack>

        <SubmitButton
          isLoading={withdrawingLiquidity || isLoading || withdrawingFromGamma}
          label="Withdraw"
          onClick={async () => {
            if (data.vault.provider === 'Ichi') {
              await withdrawFromIchi({
                address,
                vaultId: data.vault.id,
                amount: parseUnits(amount, +data.vault.lpToken.decimals),
                signer
              });
            } else if (data.vault.provider === 'Steer') {
              await withdraw();
            } else if (data.vault.provider === 'Gamma') {
              await withdrawFromGamma({
                vaultId: data.vault.id,
                amount,
                address,
                signer
              });
            }

            onClose();
          }}
          disabled={+amount > +balance.toString()}
        />
      </ModalContent>
    </Modal>
  );
};
